import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  emailAddressValidator,
  passwordValidator,
} from '../../../models/schemas/Customer';
import LabeledTextbox from '../LabeledTextbox';
import Button from '../Button';
import FormErrorLabel from '../FormErrorLabel';
import ExternalLink from '../ExternalLink';
import { useMst } from '../../../models/stores/Root';

export const CustomerLoginFormTestId = 'customerloginform-input';

export type CustomerLoginFormData = {
  emailAddress: string;
  password: string;
  reCaptchaValue: string | null;
};

export type CustomerLoginFormProps = {
  autoFocus?: boolean;
  defaultEmailAddress?: string;
  formErrors?: string[];
  onValidData: (validData: CustomerLoginFormData) => Promise<void>;
};

type CustomerLoginInputForm = {
  loginEmail: string;
  password: string;
};

const CustomerLoginForm: React.FC<CustomerLoginFormProps> = ({
  defaultEmailAddress,
  formErrors,
  onValidData,
  autoFocus = false,
}: CustomerLoginFormProps) => {
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const rootStore = useMst();
  const { configuration } = rootStore;
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [captchaResponse, setCaptchaResponse] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changeCallback = (response: any) => {
    setCaptchaResponse(response);
  };
  const expiredCallback = () => setCaptchaResponse(null);
  const customerLoginFormSchema = yup.object<CustomerLoginInputForm>().shape({
    loginEmail: emailAddressValidator,
    password: passwordValidator,
  });

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(customerLoginFormSchema),
    defaultValues: {
      loginEmail: defaultEmailAddress || '',
      password: '',
    },
  });

  type Theme = 'light' | 'dark';
  type Size = 'compact' | 'normal' | 'invisible';
  type Badge = 'bottomright' | 'bottomleft' | 'inline';

  const handleOnSubmit = async (
    data: CustomerLoginInputForm
  ): Promise<void> => {
    let reCaptchaValue = null;
    if (
      reCaptchaRef.current &&
      configuration.reCaptcha.loginEnabled &&
      configuration.reCaptcha.size === 'invisible'
    ) {
      reCaptchaValue = await reCaptchaRef.current.executeAsync();
      reCaptchaRef.current.reset();
    }
    setIsLoginPending(true);

    const { loginEmail: emailAddress, password } = data;
    onValidData({
      emailAddress,
      password,
      reCaptchaValue,
    } as CustomerLoginFormData);
  };

  const [emailValue, setEmailValue] = useState<string>(
    defaultEmailAddress || ''
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValue(event.target.value);
  };

  useEffect(() => {
    if (formErrors?.length) {
      setIsLoginPending(false);
    }
  }, [formErrors]);

  return (
    <div
      className="pt-2"
      style={{
        width:
          configuration?.reCaptcha?.loginEnabled &&
          configuration?.reCaptcha?.size === 'normal'
            ? '304px'
            : '280px',
        margin: 'auto',
      }}
    >
      <form
        onSubmit={handleSubmit(handleOnSubmit)}
        data-testid={CustomerLoginFormTestId}
      >
        {(process.env.REACT_APP_ENV === 'QA' ||
          process.env.NODE_ENV === 'development') && (
          <DevTool control={control} placement="top-right" />
        )}
        <FormErrorLabel errors={formErrors} />
        <LabeledTextbox
          name="loginEmail"
          label="Email"
          type="email"
          autoComplete="email"
          errorLabel={errors?.loginEmail?.message}
          defaultValue={defaultEmailAddress}
          register={register}
          autoFocus={autoFocus}
          onChange={handleChange}
          onBlur={handleChange}
        />
        <LabeledTextbox
          name="password"
          label="Password"
          type="password"
          autoComplete="password"
          errorLabel={errors?.password?.message}
          register={register}
          autoFocus={!!defaultEmailAddress}
        />
        <div className="text-right pr-2">
          <ExternalLink
            href={`/login_sendpass.asp?email=${
              encodeURIComponent(emailValue) ?? ''
            }`}
            openNewWindow
          >
            Forgot Password?
          </ExternalLink>
        </div>
        {configuration.reCaptcha.loginEnabled && (
          <div className="flex justify-center">
            <ReCAPTCHA
              ref={reCaptchaRef}
              sitekey={configuration?.reCaptcha?.siteKey as string}
              size={configuration?.reCaptcha?.size as Size}
              hl={configuration?.reCaptcha?.hl as string}
              theme={configuration?.reCaptcha?.theme as Theme}
              badge={configuration?.reCaptcha?.badge as Badge}
              onExpired={expiredCallback}
              onChange={changeCallback}
            />
          </div>
        )}
        <Button
          isOtherActionPending={isLoginPending}
          isDisabled={
            configuration?.reCaptcha?.loginEnabled &&
            configuration?.reCaptcha?.size !== 'invisible' &&
            !captchaResponse
          }
        >
          Log in
        </Button>
      </form>
    </div>
  );
};

export default CustomerLoginForm;
